import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectSelectedProductsByPassengerIndex,
  selectCatalogBySegment,
} from 'selectors/productsCartSelectors';

/**
 * useProductsCartSummary hook.
 * Provides products cart summary data (total quantity and total price)
 * Memoizes the calculation to avoid unnecessary recalculations
 *
 * @param {number} [passengerIndex] - Optional passenger index. If provided, returns totals for that passenger only
 * @param {string} [segment] - Optional segment of the journey: 'outgoing', 'incoming', or 'both' for combined totals
 * @returns {Object} Object with totalQuantity and totalPrice
 * @returns {number} returns.totalQuantity - Total quantity of products in cart
 * @returns {number} returns.totalPrice - Total price of products in cart
 */
export const useProductsCartSummary = (passengerIndex = 0, segment = 'outgoing') => {
  // Always fetch all data to avoid conditional hooks
  const selectedProductsOutgoing = useSelector(
    selectSelectedProductsByPassengerIndex(passengerIndex, 'outgoing'),
  );
  const selectedProductsIncoming = useSelector(
    selectSelectedProductsByPassengerIndex(passengerIndex, 'incoming'),
  );
  const catalogOutgoing = useSelector(selectCatalogBySegment('outgoing'));
  const catalogIncoming = useSelector(selectCatalogBySegment('incoming'));

  return useMemo(() => {
    /**
     * Calculate totals from products and catalog.
     * @param {Array} products - Array of selected products
     * @param {Array} cat - Catalog with price information
     * @returns {Object} Object with totalQuantity and totalPrice
     */
    const calculateTotals = (products, cat) => {
      const catalogMap = cat.reduce((acc, item) => {
        acc[item.code] = item.price;
        return acc;
      }, {});

      return products.reduce(
        (acc, product) => {
          const price = catalogMap[product.code] || 0;
          acc.totalQuantity += product.quantity || 0;
          acc.totalPrice += price * (product.quantity || 0);
          return acc;
        },
        { totalQuantity: 0, totalPrice: 0 },
      );
    };

    if (segment === 'both') {
      const totalsOutgoing = calculateTotals(selectedProductsOutgoing, catalogOutgoing);
      const totalsIncoming = calculateTotals(selectedProductsIncoming, catalogIncoming);
      return {
        totalQuantity: totalsOutgoing.totalQuantity + totalsIncoming.totalQuantity,
        totalPrice: totalsOutgoing.totalPrice + totalsIncoming.totalPrice,
      };
    }

    // For single segment, use the requested segment data
    const selectedProducts =
      segment === 'outgoing' ? selectedProductsOutgoing : selectedProductsIncoming;
    const catalog = segment === 'outgoing' ? catalogOutgoing : catalogIncoming;
    return calculateTotals(selectedProducts, catalog);
  }, [
    segment,
    selectedProductsOutgoing,
    selectedProductsIncoming,
    catalogOutgoing,
    catalogIncoming,
  ]);
};

export default useProductsCartSummary;
